// ============================================================================
// Feuille de brouillon : HÉRÉDITÉ d'une récurrence jugée par le noyau
// (moteur v26, refonte lot 5.5 — docs/moteur/REFONTE_MOTEUR.md).
// ============================================================================
// L'énoncé : la définition de la suite (`kernel.start`, « uₙ₊₁ = 3uₙ »), d'autres
// données (`givens`) et l'HYPOTHÈSE DE RÉCURRENCE P(n) (`kernel.induction.hypothesis` :
// « uₙ = 3ⁿ », « 0 ≤ uₙ ≤ 2 », « 4ⁿ − 1 = 3k »). La réponse : P(n + 1).
//
// Chaque ligne est une relation (ou une chaîne « a = b = c », « 0 ≤ uₙ₊₁ ≤ 2 »). Elle doit
// être VRAIE sachant l'énoncé — les grandeurs définies par une égalité (uₙ₊₁ par la
// définition, uₙ ou k par l'hypothèse) sont remplacées par ce qu'elles valent ; une
// hypothèse en inégalités contraint uₙ — puis JUSTIFIÉE en un pas :
//   - un calcul (identité vraie pour tout n, qui porte sur la démonstration) : 1 HORA ;
//   - remplacer avec l'hypothèse de récurrence : 3 HORA (le cœur de l'hérédité) ;
//   - remplacer avec la définition de la suite : 2 HORA ;
//   - remplacer avec une donnée ou une ligne écrite (enchaîner les égalités) : 1 HORA ;
//   - transformer une relation déjà établie (ajouter, diviser, appliquer une fonction
//     croissante…) : la règle du paquet du noyau, 2 HORA ; élargir une inégalité : 1 HORA.
// Écrire P(n + 1) avant de l'avoir obtenu : « C'est le but de l'hérédité ».
// Le corrigé (`kernel.path`) est obligatoire : il sert au coup de pouce.
// ============================================================================

import { checkSyntax, formatNumber, lex, normalize, normalizeChips, type Chip } from './lexer';
import type { KernelHypothesis, KernelSpec, ScratchModel } from './model';
import { atomsText, parseLine, parseText, toAtoms, type Eq, type Line } from './tree';
import { checkAlgebraStatements } from './kernel/plugins/algebra';
import { compareTerms, evalFloat } from './kernel/normal';
import {
  canon,
  div,
  fn,
  fromNode,
  neg,
  num,
  sub,
  substitute,
  symbolsOf,
  termKey,
  type FnName,
  type Rel,
  type Relation,
  type Statement,
  type Term,
} from './kernel/term';
import type { Condition, StepCheck } from './kernel/types';
import { kernelRuleStep, kernelRules, kernelRulesFor, type StepInfo } from './kernel-sheet';

/** Identifiants des pas de la récurrence (ceux qui ne sont pas des règles d'un paquet). */
export const RECURRENCE_STEP_IDS = {
  /** remplacer avec l'hypothèse de récurrence */
  hypothesis: 'induction.hypothesis@1',
  /** remplacer avec la définition de la suite */
  definition: 'induction.definition@1',
  /** remplacer avec une donnée, ou enchaîner les égalités */
  substitute: 'kernel.substitute@1',
  /** calculer (identité) */
  compute: 'kernel.compute@1',
  /** appliquer une fonction croissante aux deux membres */
  increasing: 'induction.increasing@1',
  /** élargir une inégalité, un encadrement */
  weaken: 'induction.weaken@1',
} as const;

const CARD = 'Raisonnement par récurrence';

export interface KRecVerdict {
  kind:
    | 'empty'
    | 'open'
    | 'building'
    | 'ready'
    | 'pending'
    | 'syntax'
    | 'trap'
    | 'false'
    | 'unjustified'
    | 'useless'
    | 'step';
  atoms: string[];
  title?: string;
  message?: string;
  badChip?: number;
  complete?: boolean;
  line?: Line;
  final?: boolean;
  hora?: number;
  trap?: { line: Line; title: string; why: string };
  rule?: { id: string; card: string; label: string; hora: number; why: string };
  /** le noyau ne sait pas vérifier ce passage */
  unknown?: boolean;
  /** la ligne lue (enregistrée par `krecCommit`) */
  rfact?: RFact;
}

/** Une relation d'une ligne, avec les textes de ses membres (pour les messages). */
interface RRel {
  rel: Rel;
  l: Term;
  r: Term;
  lText: string;
  rText: string;
  key: string;
}

type Source = 'def' | 'given' | 'hr' | 'line';

/** Une ligne lue : une relation, une chaîne d'égalités ou un encadrement. */
export interface RFact {
  text: string;
  src: Source;
  /** relations consécutives écrites */
  rels: RRel[];
  /** tout ce que la ligne établit (chaîne d'égalités : toutes les paires) */
  pairs: RRel[];
  /** unités jugées une à une : chaque maillon d'une chaîne d'égalités, un encadrement entier */
  units: RRel[][];
  /**
   * ligne obtenue en transformant l'hypothèse de récurrence (« 4ⁿ = 3k + 1 ») : remplacer
   * avec elle, c'est encore utiliser l'hypothèse
   */
  origin?: Source;
}

export interface KRecState {
  spec: KernelSpec;
  /** la variable de la récurrence (n) */
  x: string;
  hyps: Condition[];
  /** énoncé : définition, données, hypothèse de récurrence */
  facts: RFact[];
  lines: RFact[];
  hr: RFact;
  answer: RFact;
  traps: { fact: RFact; title: string; why: string; line: Line }[];
  path: string[];
  /** grandeurs définies par une égalité de l'énoncé : uₙ₊₁ → 3uₙ, uₙ → 3ⁿ, k → (4ⁿ − 1) ÷ 3 */
  defs: Map<string, Term>;
  /** hypothèse en inégalités (après remplacement des grandeurs définies) */
  cons: Statement | null;
  /** grandeurs contraintes par `cons` (uₙ) */
  constrained: Set<string>;
  /** valeurs d'essai des grandeurs libres (n, k…) */
  envs: Record<string, number>[];
  /** valeurs d'essai qui respectent aussi l'hypothèse en inégalités (uₙ entre 0 et 2…) */
  consEnvs: Record<string, number>[];
  known: string[][] | null;
  cache: Map<string, StepCheck>;
}

/* ------------------------------ lecture ------------------------------ */

export class RecurrenceSyntaxError extends Error {}

const FLIP: Record<Rel, Rel> = { '=': '=', '<': '>', '>': '<', '≤': '≥', '≥': '≤' };

function relKey(rel: Rel, l: Term, r: Term): string {
  let a = termKey(canon(l));
  let b = termKey(canon(r));
  let op = rel;
  if (op === '>' || op === '≥') {
    [a, b] = [b, a];
    op = FLIP[op];
  }
  if (op === '=' && a > b) [a, b] = [b, a];
  return `${op}:${a}|${b}`;
}
const unitKey = (u: RRel[]): string =>
  u
    .map((r) => r.key)
    .sort()
    .join('&');

function mkRel(rel: Rel, l: Term, r: Term, lText: string, rText: string): RRel {
  return { rel, l, r, lText, rText, key: relKey(rel, l, r) };
}

function factOfEq(d: Eq, text: string, src: Source): RFact {
  if (d.rels || d.mod !== undefined)
    throw new RecurrenceSyntaxError('Une congruence n’a pas sa place ici.');
  if (d.sides.length < 2) throw new RecurrenceSyntaxError('Écris une égalité ou une inégalité.');
  let T: Term[];
  try {
    T = d.sides.map(fromNode);
  } catch {
    throw new RecurrenceSyntaxError('Je ne sais pas lire cette écriture.');
  }
  const texts = d.sides.map((s) => atomsText(toAtoms(s)));
  if (d.cmp === '≈') throw new RecurrenceSyntaxError('Pas de valeur approchée : on démontre.');
  if (d.chain || d.cmp) {
    const ops = d.chain ?? [d.cmp as Rel];
    const rels = ops.map((op, i) => mkRel(op, T[i]!, T[i + 1]!, texts[i]!, texts[i + 1]!));
    return { text, src, rels, pairs: rels, units: [rels] };
  }
  const rels = T.slice(1).map((t, i) => mkRel('=', T[i]!, t, texts[i]!, texts[i + 1]!));
  const pairs: RRel[] = [];
  for (let i = 0; i < T.length; i++)
    for (let j = i + 1; j < T.length; j++)
      pairs.push(mkRel('=', T[i]!, T[j]!, texts[i]!, texts[j]!));
  return { text, src, rels, pairs, units: rels.map((r) => [r]) };
}

/** Lit une ligne écrite en texte (énoncé, corrigé, piège, choix). */
export function krecReadFact(text: string, src: Source = 'line'): RFact {
  let line: Line;
  try {
    line = parseText(text);
  } catch {
    throw new RecurrenceSyntaxError('Ligne illisible.');
  }
  if (line.dis.length !== 1) throw new RecurrenceSyntaxError('Une seule relation par ligne.');
  return factOfEq(line.dis[0]!, text, src);
}

/** Les bouts d'une ligne écrite (énoncé, corrigé, coup de pouce). */
export function krecLineAtoms(text: string): string[] {
  return normalize(lex(text));
}

/* ------------------------------ état ------------------------------ */

function hypothesisCondition(h: KernelHypothesis): Condition {
  return { kind: h.kind === 'acute' || h.kind === 'angle' ? 'positive' : h.kind, of: [h.of] };
}

export function createKRecState(model: ScratchModel): KRecState {
  const spec = model.kernel!;
  const ind = spec.induction!;
  const x = ind.variable ?? 'n';
  const hrText = ind.hypothesis;
  const facts: RFact[] = [];
  const hr = krecReadFact(hrText, 'hr');
  if (spec.start.trim() !== hrText.trim()) facts.push(krecReadFact(spec.start, 'def'));
  (model.givens ?? []).forEach((t) => facts.push(krecReadFact(t, 'given')));
  facts.push(hr);
  const hyps = (spec.hypotheses ?? []).map(hypothesisCondition);
  if (!hyps.some((h) => h.of[0] === x)) hyps.unshift({ kind: 'natural', of: [x] });
  const ks: KRecState = {
    spec,
    x,
    hyps,
    facts,
    lines: [],
    hr,
    answer: krecReadFact(spec.answer, 'line'),
    traps: (model.traps ?? []).flatMap((t) => {
      try {
        return [
          { fact: krecReadFact(t.line), title: t.title, why: t.why, line: parseText(t.line) },
        ];
      } catch {
        return [];
      }
    }),
    path: spec.path ?? [],
    defs: new Map(),
    cons: null,
    constrained: new Set(),
    envs: [],
    consEnvs: [],
    known: null,
    cache: new Map(),
  };
  buildDefs(ks);
  ks.envs = sampleEnvs(ks, model);
  ks.consEnvs = constrainedEnvs(ks);
  // les paquets de règles chargés tout de suite (pas pendant le premier jugement)
  kernelRules();
  return ks;
}

/** Remplace les grandeurs définies jusqu'à ce qu'il n'en reste plus (8 tours au plus). */
function elim(ks: KRecState, t: Term): Term {
  let cur = t;
  for (let i = 0; i < 8; i++) {
    const s = [...symbolsOf(cur)].filter((n) => ks.defs.has(n));
    if (!s.length) return cur;
    const map: Record<string, Term> = {};
    s.forEach((n) => (map[n] = ks.defs.get(n)!));
    cur = substitute(cur, map);
  }
  return cur;
}

const isZero = (t: Term): boolean => compareTerms(t, num(0)) === 'equal';

/** s définie par l = r, si l = r se résout en s au premier degré : s = −f(0) ÷ (f(1) − f(0)) */
function solveLinear(f: Term, s: string): Term | null {
  const at = (v: number): Term => substitute(f, { [s]: num(v) });
  const f0 = at(0);
  const a = sub(at(1), f0);
  if (isZero(a) || compareTerms(sub(at(2), at(1)), a) !== 'equal') return null;
  if (symbolsOf(a).has(s)) return null;
  return canon(div(neg(f0), a));
}

function buildDefs(ks: KRecState): void {
  // l'hypothèse d'abord (elle définit uₙ, ou k), puis la définition de la suite, les données
  const order = [ks.hr, ...ks.facts.filter((f) => f !== ks.hr)];
  const ineq: Relation[] = [];
  for (const f of order) {
    for (const r of f.rels) {
      if (r.rel !== '=') {
        if (f.src === 'hr' || f.src === 'given') ineq.push({ rel: r.rel, l: r.l, r: r.r });
        continue;
      }
      define(ks, r);
    }
  }
  if (ineq.length) {
    const rels = ineq.map((r) => ({ rel: r.rel, l: elim(ks, r.l), r: elim(ks, r.r) }));
    ks.cons = rels.length === 1 ? { k: 'rel', ...rels[0]! } : { k: 'set', clauses: [rels] };
    rels.forEach((r) => {
      symbolsOf(r.l, ks.constrained);
      symbolsOf(r.r, ks.constrained);
    });
    ks.constrained.delete(ks.x);
  }
}

function define(ks: KRecState, r: RRel): void {
  const free = (n: string): boolean => n !== ks.x && !ks.defs.has(n) && n !== 'π';
  for (const [a, b] of [
    [r.l, r.r],
    [r.r, r.l],
  ] as [Term, Term][]) {
    if (a.k === 'sym' && free(a.n)) {
      const e = elim(ks, b);
      if (!symbolsOf(e).has(a.n)) {
        ks.defs.set(a.n, b);
        return;
      }
    }
  }
  const f = canon(sub(r.l, r.r));
  for (const s of symbolsOf(f)) {
    if (!free(s)) continue;
    const e = solveLinear(f, s);
    if (e && !symbolsOf(elim(ks, e)).has(s)) {
      ks.defs.set(s, e);
      return;
    }
  }
}

/** valeurs d'essai des grandeurs libres : n entier naturel, k entier, a positif… */
function sampleEnvs(ks: KRecState, model: ScratchModel): Record<string, number>[] {
  const from = ks.spec.induction?.from ?? 0;
  const syms = new Set<string>();
  const add = (f: RFact) =>
    f.pairs.forEach((r) => {
      symbolsOf(elim(ks, r.l), syms);
      symbolsOf(elim(ks, r.r), syms);
    });
  [...ks.facts, ks.answer].forEach(add);
  ks.path.forEach((t) => {
    try {
      add(krecReadFact(t));
    } catch {
      /* déjà signalé par la porte */
    }
  });
  Object.keys(model.symbols).forEach((n) => syms.add(n));
  const free = [...syms].filter(
    (n) => n !== 'π' && !ks.defs.has(n) && !ks.constrained.has(n) && /^[^?]/.test(n),
  );
  const kindOf = (n: string): string =>
    ks.hyps.find((h) => h.of.length === 1 && h.of[0] === n)?.kind ?? 'real';
  const values = (n: string): number[] => {
    switch (n === ks.x ? 'natural' : kindOf(n)) {
      case 'natural':
        return n === ks.x ? [0, 1, 2, 3, 4, 5, 7].map((v) => v + from) : [0, 1, 2, 3, 5];
      case 'integer':
        return [-2, -1, 0, 1, 2, 3];
      case 'positive':
        return [0.5, 1, 2.5, 4];
      case 'nonnegative':
        return [0, 0.5, 2, 3.5];
      case 'negative':
        return [-0.5, -1, -2.5];
      default:
        return [-1.5, 0.5, 2, 3.25];
    }
  };
  let envs: Record<string, number>[] = [{}];
  for (const n of free) {
    const next: Record<string, number>[] = [];
    for (const e of envs) for (const v of values(n)) next.push({ ...e, [n]: v });
    envs = next.slice(0, 96);
  }
  return envs;
}

/** valeurs d'essai des grandeurs contraintes, gardées si elles respectent l'hypothèse */
function constrainedEnvs(ks: KRecState): Record<string, number>[] {
  if (!ks.cons || !ks.constrained.size || ks.constrained.size > 2) return [];
  const grid: number[] = [];
  for (let v = -6; v <= 12; v += 0.37) grid.push(Math.round(v * 1000) / 1000);
  [0, 1, 2, 3, 4, 5, Math.SQRT2].forEach((v) => grid.push(v));
  const names = [...ks.constrained];
  const rels = ks.cons.k === 'rel' ? [ks.cons] : ks.cons.k === 'set' ? ks.cons.clauses[0]! : [];
  const out: Record<string, number>[] = [];
  for (const base of ks.envs.slice(0, 6)) {
    const pts =
      names.length === 1
        ? grid.map((v) => [v])
        : grid.flatMap((a) => grid.filter((_, i) => i % 4 === 0).map((b) => [a, b]));
    for (const p of pts) {
      const env = { ...base };
      names.forEach((n, i) => (env[n] = p[i]!));
      if (rels.every((r) => holdsNum(r.rel, evalFloat(r.l, env), evalFloat(r.r, env))))
        out.push(env);
    }
  }
  return out.slice(0, 400);
}

/* ------------------------------ vérité ------------------------------ */

type Truth =
  | { v: 'true' }
  | { v: 'unknown' }
  | { v: 'false'; env?: Record<string, number>; rel?: RRel; lv?: number; rv?: number; at?: string };

function holdsNum(rel: Rel, a: number, b: number): boolean {
  const eps = 1e-9 * (1 + Math.abs(a) + Math.abs(b));
  switch (rel) {
    case '=':
      return Math.abs(a - b) <= eps;
    case '<':
      return a < b - eps;
    case '>':
      return a > b + eps;
    case '≤':
      return a <= b + eps;
    case '≥':
      return a >= b - eps;
  }
}

function checkCached(ks: KRecState, from: Statement, to: Statement): StepCheck {
  const key = `${JSON.stringify(from, big)}→${JSON.stringify(to, big)}`;
  const hit = ks.cache.get(key);
  if (hit) return hit;
  const c = checkAlgebraStatements(from, to, {
    rules: kernelRulesFor(false),
    hypotheses: ks.hyps,
    deadline: Date.now() + 200,
    branches: false,
  });
  ks.cache.set(key, c);
  return c;
}
const big = (_k: string, v: unknown): unknown => (typeof v === 'bigint' ? v.toString() : v);

const stmtOf = (rels: Relation[]): Statement =>
  rels.length === 1 ? { k: 'rel', ...rels[0]! } : { k: 'set', clauses: [rels] };

/** La relation est-elle vraie, sachant l'énoncé (hypothèse de récurrence comprise) ? */
function truthOf(ks: KRecState, unit: RRel[]): Truth {
  const er = unit.map((r) => ({ src: r, rel: r.rel, l: elim(ks, r.l), r: elim(ks, r.r) }));
  const pending: typeof er = [];
  for (const r of er) {
    if (r.rel === '=' && compareTerms(r.l, r.r) === 'equal') continue;
    pending.push(r);
  }
  if (!pending.length) return { v: 'true' };
  const touches = (r: (typeof er)[number]): boolean =>
    [...symbolsOf(r.l), ...symbolsOf(r.r)].some((n) => ks.constrained.has(n));
  if (ks.cons && pending.some(touches)) {
    const c = checkCached(ks, ks.cons, stmtOf(pending));
    if (c.status === 'valid' && c.relation !== 'none') return { v: 'true' };
    if (c.status === 'invalid') return { v: 'false', at: c.explanation?.params?.at };
    // le noyau ne tranche pas : des valeurs d'essai qui respectent l'hypothèse
    if (!ks.consEnvs.length) return { v: 'unknown' };
  }
  let tried = 0;
  const envs = ks.cons && pending.some(touches) ? ks.consEnvs : ks.envs;
  for (const env of envs) {
    for (const r of pending) {
      const lv = evalFloat(r.l, env);
      const rv = evalFloat(r.r, env);
      if (!Number.isFinite(lv) || !Number.isFinite(rv)) continue;
      tried++;
      if (!holdsNum(r.rel, lv, rv)) return { v: 'false', env, rel: r.src, lv, rv };
    }
  }
  return tried ? { v: 'true' } : { v: 'unknown' };
}

/* ------------------------------ juge ------------------------------ */

function knownAtoms(ks: KRecState): string[][] {
  ks.known ??= [...ks.path, ks.spec.answer].flatMap((t) => {
    try {
      return [krecLineAtoms(t)];
    } catch {
      return [];
    }
  });
  return ks.known;
}
function strictPrefixOfKnown(ks: KRecState, A: string[]): boolean {
  return knownAtoms(ks).some((k) => k.length > A.length && A.every((a, i) => k[i] === a));
}

export function krecJudge(ks: KRecState, chips: Chip[], opts?: { verify?: boolean }): KRecVerdict {
  const norm = normalizeChips(chips);
  const A = norm.atoms;
  if (!A.length) return { kind: 'empty', atoms: A, badChip: -1 };
  const syn = checkSyntax(A, { chains: true });
  const prefix = strictPrefixOfKnown(ks, A);
  if (!syn.ok) {
    if (prefix) return { kind: 'building', atoms: A, complete: false };
    return {
      kind: 'syntax',
      atoms: A,
      title: 'Ça commence mal',
      message: syn.msg,
      badChip: norm.src[syn.index] !== undefined ? norm.src[syn.index]! : chips.length - 1,
    };
  }
  if (!syn.complete || !syn.hasEq)
    return { kind: prefix ? 'building' : 'open', atoms: A, complete: false };
  let E: Line;
  let fact: RFact;
  try {
    E = parseLine(A, { chains: true });
    if (E.dis.length !== 1) throw new RecurrenceSyntaxError('Une seule relation par ligne.');
    fact = factOfEq(E.dis[0]!, atomsText(A), 'line');
  } catch (e) {
    if (prefix) return { kind: 'building', atoms: A, complete: false };
    if (!(e instanceof RecurrenceSyntaxError)) return { kind: 'open', atoms: A, complete: false };
    return {
      kind: 'syntax',
      atoms: A,
      title: 'Ça commence mal',
      message: e.message,
      badChip: chips.length - 1,
    };
  }
  const v = evaluate(ks, fact, A, E);
  if (prefix && !opts?.verify) {
    if (v.kind === 'step') return { ...v, kind: 'ready' };
    return { kind: 'building', atoms: A, complete: true };
  }
  if (!opts?.verify && (v.kind === 'unjustified' || v.kind === 'false'))
    return { kind: 'pending', atoms: A, complete: true };
  return v;
}

const lower = (s: string): string => s.charAt(0).toLowerCase() + s.slice(1);

/** clés de tout ce qui est établi (énoncé + lignes, + des relations en plus) */
function establishedKeys(ks: KRecState, extra: RRel[] = []): Set<string> {
  const out = new Set<string>();
  [...ks.facts, ...ks.lines].forEach((f) => f.pairs.forEach((r) => out.add(r.key)));
  extra.forEach((r) => out.add(r.key));
  return out;
}

interface UnitResult {
  kind: 'known' | 'step' | 'false' | 'unjustified' | 'unknown' | 'useless';
  info?: StepInfo;
  /** pas : transformation d'une relation de cette provenance */
  from?: Source;
  title?: string;
  message?: string;
}

function evaluate(ks: KRecState, fact: RFact, A: string[], line: Line): KRecVerdict {
  // 1. un piège du modèle
  const fk = unitKey(fact.rels);
  const trap = ks.traps.find((t) => unitKey(t.fact.rels) === fk);
  if (trap)
    return {
      kind: 'trap',
      atoms: A,
      line,
      title: 'Piège : ' + lower(trap.title),
      message: trap.why,
      trap: { line: trap.line, title: trap.title, why: trap.why },
      badChip: -1,
    };
  // 2. chaque maillon, les précédents de la ligne déjà acquis
  const done: RRel[] = [];
  const steps: StepInfo[] = [];
  const froms: (Source | undefined)[] = [];
  let useless: UnitResult | null = null;
  for (const unit of fact.units) {
    const res = evaluateUnit(ks, unit, done, fact);
    if (res.kind === 'false' || res.kind === 'unjustified')
      return { kind: res.kind, atoms: A, line, title: res.title, message: res.message };
    if (res.kind === 'unknown')
      return {
        kind: 'useless',
        atoms: A,
        line,
        unknown: true,
        title: 'Je ne sais pas encore vérifier ce passage',
        message:
          'Ta ligne est peut-être juste, mais je ne sais pas la vérifier. Garde-la sans HORA, ou écris le passage autrement.',
      };
    if (res.kind === 'useless') useless ??= res;
    if (res.kind === 'step' && res.info) {
      steps.push(res.info);
      froms.push(res.from);
    }
    done.push(...unit);
  }
  if (!steps.length) {
    const u = useless ?? knownResult(ks, fact);
    return { kind: 'useless', atoms: A, line, title: u.title, message: u.message, rfact: fact };
  }
  const best = steps.reduce((a, b) => (b.hora > a.hora ? b : a));
  const rfact: RFact = froms.every((f) => f === 'hr') ? { ...fact, origin: 'hr' } : fact;
  const hora = steps.reduce((s, x) => s + x.hora, 0);
  const final = reachesGoal(ks, fact);
  const why = final
    ? best.why + ' C’est P(n + 1) : l’hérédité est démontrée.'
    : steps.length > 1
      ? steps.map((s) => s.why).join(' ')
      : best.why;
  return {
    kind: 'step',
    atoms: A,
    line,
    final,
    hora,
    rule: { id: best.id, card: best.card, label: best.label, hora, why },
    rfact,
  };
}

function knownResult(ks: KRecState, fact: RFact): UnitResult {
  const k = unitKey(fact.rels);
  if (unitKey(ks.hr.rels) === k)
    return {
      kind: 'useless',
      title: 'Tu le sais déjà',
      message: 'C’est l’hypothèse de récurrence : sers-t’en pour remplacer.',
    };
  const def = ks.facts.find((f) => f.src === 'def');
  if (def && unitKey(def.rels) === k)
    return {
      kind: 'useless',
      title: 'Tu le sais déjà',
      message: 'C’est la définition de la suite, dans l’énoncé.',
    };
  return {
    kind: 'useless',
    title: 'Tu le sais déjà',
    message: 'C’est déjà dans l’énoncé ou sur ta feuille.',
  };
}

function evaluateUnit(ks: KRecState, unit: RRel[], done: RRel[], fact: RFact): UnitResult {
  const est = establishedKeys(ks, done);
  if (unit.every((r) => est.has(r.key))) return { kind: 'known' };
  // vrai ?
  const t = truthOf(ks, unit);
  if (t.v === 'false') return { kind: 'false', title: 'C’est faux', message: falseMessage(ks, t) };
  // un calcul (identité) ?
  const id = identityStep(ks, unit, done);
  if (id) return id;
  // remplacer avec une égalité établie ?
  const sub = substitutionStep(ks, unit, done);
  if (sub) return { kind: 'step', info: sub };
  // transformer une relation établie ?
  const tr = transformStep(ks, unit, done);
  if (tr && tr !== 'jump') return { kind: 'step', info: tr.info, from: tr.from };
  if (t.v === 'unknown' && !tr) return { kind: 'unknown' };
  if (isAnswerUnit(ks, unit, fact))
    return {
      kind: 'unjustified',
      title: 'C’est le but de l’hérédité',
      message:
        'Cette ligne, c’est P(n + 1) : ce qu’il faut démontrer, pas un point de départ. Pars de la définition de la suite, remplace avec l’hypothèse de récurrence, puis calcule jusqu’à elle.',
    };
  if (tr === 'jump')
    return {
      kind: 'unjustified',
      title: 'Une étape à la fois',
      message:
        'Ta ligne est juste, mais elle fait plusieurs choses d’un coup. Écris une étape à la fois : remplacer, développer, réduire, factoriser…',
    };
  return {
    kind: 'unjustified',
    title: 'D’où vient-elle ?',
    message:
      'C’est juste, mais ça ne découle pas en un pas de ce qui est écrit. Remplace avec l’hypothèse de récurrence ou la définition de la suite, ou transforme une ligne déjà écrite — une étape à la fois.',
  };
}

function falseMessage(ks: KRecState, t: Extract<Truth, { v: 'false' }>): string {
  if (t.env && t.rel && t.lv !== undefined && t.rv !== undefined) {
    const at = Object.entries(t.env)
      .filter(([n]) => n === ks.x || ks.hyps.some((h) => h.of[0] === n))
      .map(([n, v]) => `${n} = ${formatNumber(v)}`)
      .join(', ');
    const where = at ? `Pour ${at}` : 'Avec les valeurs de l’énoncé';
    const lc = !symbolsOf(t.rel.l).size;
    const rc = !symbolsOf(t.rel.r).size;
    const L = lc ? t.rel.lText : `${t.rel.lText} vaut ${formatNumber(t.lv)}`;
    const R = rc ? t.rel.rText : `${t.rel.rText} vaut ${formatNumber(t.rv)}`;
    const not = t.rel.rel === '=' ? 'ce n’est pas égal' : 'l’inégalité n’est pas vérifiée';
    return `${where}, ${L} et ${R} : ${not}. Vérifie ton calcul (et l’hypothèse de récurrence).`;
  }
  if (t.at)
    return `Ça ne découle pas de l’hypothèse de récurrence : essaie ${t.at}. Vérifie chaque inégalité.`;
  return 'Ta ligne ne découle pas de l’énoncé et de l’hypothèse de récurrence.';
}

/* ---- calcul ---- */

/** membres (clés) de tout ce qui est écrit, et de la réponse */
function memberKeys(ks: KRecState, done: RRel[]): Set<string> {
  const out = new Set<string>();
  const addRel = (r: RRel) => {
    out.add(termKey(canon(r.l)));
    out.add(termKey(canon(r.r)));
  };
  [...ks.facts, ...ks.lines, ks.answer].forEach((f) => f.pairs.forEach(addRel));
  done.forEach(addRel);
  return out;
}

function identityStep(ks: KRecState, unit: RRel[], done: RRel[]): UnitResult | null {
  const isId = unit.every((r) => {
    if (r.rel === '=') return compareTerms(r.l, r.r) === 'equal';
    const syms = [...symbolsOf(r.l), ...symbolsOf(r.r)].filter((n) => n !== 'π');
    if (syms.length) return false;
    return holdsNum(r.rel, evalFloat(r.l, {}), evalFloat(r.r, {}));
  });
  if (!isId) return null;
  const members = memberKeys(ks, done);
  const relevant = unit.some((r) => {
    const a = termKey(canon(r.l));
    const b = termKey(canon(r.r));
    return a !== b && (members.has(a) || members.has(b));
  });
  if (!relevant)
    return {
      kind: 'useless',
      title: 'Juste, mais à côté',
      message:
        'Ce calcul est juste, mais il ne sert pas ta démonstration. Pars de ce qui est écrit : la définition de la suite, l’hypothèse de récurrence, ou ta dernière ligne.',
    };
  return {
    kind: 'step',
    info: {
      id: RECURRENCE_STEP_IDS.compute,
      card: CARD,
      label: 'Calculer',
      hora: 1,
      why: 'Tu écris la même quantité autrement : c’est un calcul, vrai pour tout n.',
    },
  };
}

/* ---- remplacer ---- */

function replaceIn(t: Term, pKey: string, q: Term): { t: Term; n: number } {
  if (termKey(t) === pKey) return { t: q, n: 1 };
  switch (t.k) {
    case 'add':
    case 'mul': {
      let n = 0;
      const c = t.c.map((x) => {
        const r = replaceIn(x, pKey, q);
        n += r.n;
        return r.t;
      });
      return { t: { k: t.k, c }, n };
    }
    case 'div':
    case 'pow':
    case 'binom': {
      const a = replaceIn(t.a, pKey, q);
      const b = replaceIn(t.b, pKey, q);
      return { t: { k: t.k, a: a.t, b: b.t }, n: a.n + b.n };
    }
    case 'fn': {
      const a = replaceIn(t.a, pKey, q);
      return { t: { k: 'fn', f: t.f, a: a.t }, n: a.n };
    }
    default:
      return { t, n: 0 };
  }
}

interface Eqn {
  rel: RRel;
  src: Source;
  text: string;
}

/** égalités établies, chacune avec sa provenance */
function equalities(ks: KRecState, done: RRel[]): Eqn[] {
  const out: Eqn[] = [];
  [...ks.facts, ...ks.lines].forEach((f) =>
    f.pairs.forEach((r) => {
      if (r.rel === '=') out.push({ rel: r, src: f.origin ?? f.src, text: f.text });
    }),
  );
  done.forEach((r) => {
    if (r.rel === '=') out.push({ rel: r, src: 'line', text: `${r.lText} = ${r.rText}` });
  });
  return out;
}

/** relations établies, une par une, et les encadrements entiers */
function establishedUnits(ks: KRecState, done: RRel[]): { rels: RRel[]; src: Source }[] {
  const out: { rels: RRel[]; src: Source }[] = [];
  [...ks.facts, ...ks.lines].forEach((f) => {
    const src = f.origin ?? f.src;
    f.pairs.forEach((r) => out.push({ rels: [r], src }));
    if (f.units.length === 1 && f.units[0]!.length > 1) out.push({ rels: f.units[0]!, src });
  });
  done.forEach((r) => out.push({ rels: [r], src: 'line' }));
  return out;
}

function substitutionInfo(e: Eqn, whole: boolean): StepInfo {
  const by = `${e.rel.lText} = ${e.rel.rText}`;
  if (e.src === 'hr')
    return {
      id: RECURRENCE_STEP_IDS.hypothesis,
      card: CARD,
      label: 'Utiliser l’hypothèse de récurrence',
      hora: 3,
      why: `On remplace grâce à l’hypothèse de récurrence ${by} : c’est là qu’elle sert.`,
    };
  if (e.src === 'def')
    return {
      id: RECURRENCE_STEP_IDS.definition,
      card: CARD,
      label: 'Utiliser la définition de la suite',
      hora: 2,
      why: `On remplace grâce à la définition de la suite : ${by}.`,
    };
  return {
    id: RECURRENCE_STEP_IDS.substitute,
    card: CARD,
    label: whole && e.src === 'line' ? 'Enchaîner les égalités' : 'Remplacer',
    hora: 1,
    why:
      whole && e.src === 'line'
        ? `On enchaîne avec ${by}, écrite plus haut.`
        : `On remplace grâce à ${by}.`,
  };
}

function substitutionStep(ks: KRecState, unit: RRel[], done: RRel[]): StepInfo | null {
  const want = unitKey(unit);
  const eqs = equalities(ks, done);
  const units = establishedUnits(ks, done);
  // la provenance la plus parlante d'abord : l'hypothèse, puis la définition
  const rank: Record<Source, number> = { hr: 0, def: 1, given: 2, line: 3 };
  eqs.sort((a, b) => rank[a.src] - rank[b.src]);
  for (const e of eqs) {
    for (const [p, q] of [
      [e.rel.l, e.rel.r],
      [e.rel.r, e.rel.l],
    ] as [Term, Term][]) {
      if (!symbolsOf(p).size) continue;
      const pc = canon(p);
      const pKey = termKey(pc);
      const qc = canon(q);
      // (a) une relation établie, dans laquelle on remplace
      for (const u of units) {
        if (u.rels.some((r) => r.key === e.rel.key)) continue;
        let n = 0;
        const rels = u.rels.map((r) => {
          const a = replaceIn(canon(r.l), pKey, qc);
          const b = replaceIn(canon(r.r), pKey, qc);
          n += a.n + b.n;
          return mkRel(r.rel, canon(a.t), canon(b.t), '', '');
        });
        if (n && unitKey(rels) === want) {
          const whole = u.rels.some(
            (r) => termKey(canon(r.l)) === pKey || termKey(canon(r.r)) === pKey,
          );
          return substitutionInfo(e, whole);
        }
      }
      // (b) la ligne elle-même devient une égalité évidente — et elle porte sur la
      // démonstration (un de ses membres est déjà écrit ailleurs) : sinon c'est l'égalité
      // elle-même, réécrite (« 4ⁿ = 3k + 1 »), une transformation
      if (!unit.some((r) => connected(ks, done, e.rel, r))) continue;
      let n = 0;
      const trivial = unit.every((r) => {
        if (r.rel !== '=') return false;
        const a = replaceIn(canon(r.l), pKey, qc);
        const b = replaceIn(canon(r.r), pKey, qc);
        n += a.n + b.n;
        return termKey(canon(a.t)) === termKey(canon(b.t));
      });
      if (n && trivial) return substitutionInfo(e, false);
    }
  }
  return null;
}

/** un membre de la relation est-il écrit ailleurs que dans l'égalité `e` ? */
function connected(ks: KRecState, done: RRel[], e: RRel, r: RRel): boolean {
  const keys = new Set<string>();
  const add = (x: RRel) => {
    if (x.key === e.key) return;
    keys.add(termKey(canon(x.l)));
    keys.add(termKey(canon(x.r)));
  };
  [...ks.facts, ...ks.lines].forEach((f) => f.pairs.forEach(add));
  done.forEach(add);
  return keys.has(termKey(canon(r.l))) || keys.has(termKey(canon(r.r)));
}

/* ---- transformer ---- */

/** nombre de termes d'une somme (au numérateur) : développer en ajoute, factoriser en retire */
function termCount(t: Term): number {
  if (t.k === 'add') return t.c.length;
  if (t.k === 'div') return termCount(t.a);
  return 1;
}
const termsOf = (rels: readonly { l: Term; r: Term }[]): number =>
  rels.reduce((s, r) => s + termCount(canon(r.l)) + termCount(canon(r.r)), 0);

/** « Développer » à l'envers : la ligne factorise */
function ruleInfo(
  c: StepCheck,
  to: Statement,
  from: Statement,
  fromRels: RRel[],
  unit: RRel[],
): StepInfo {
  const info = kernelRuleStep(c, to, from);
  if (info.label === 'Développer' && termsOf(unit) < termsOf(fromRels))
    return {
      ...info,
      label: 'Factoriser',
      why: 'On factorise : k × a + k × b = k(a + b).',
    };
  return info;
}

const MONOTONE: { f: FnName; label: string; why: string }[] = [
  {
    f: 'sqrt',
    label: 'Appliquer la racine carrée',
    why: 'La fonction racine carrée est croissante sur [0 ; +∞[ : elle conserve l’ordre.',
  },
  {
    f: 'exp',
    label: 'Appliquer l’exponentielle',
    why: 'La fonction exponentielle est croissante : elle conserve l’ordre.',
  },
  {
    f: 'ln',
    label: 'Appliquer le logarithme',
    why: 'La fonction ln est croissante sur ]0 ; +∞[ : elle conserve l’ordre.',
  },
];

function sideSame(a: Term, b: Term): boolean {
  return termKey(canon(a)) === termKey(canon(b)) || compareTerms(a, b) === 'equal';
}

/** chaque membre de `to` est f(membre de `from`) */
function appliedFn(from: RRel[], to: RRel[]): (typeof MONOTONE)[number] | null {
  if (from.length !== to.length) return null;
  for (const m of MONOTONE) {
    const ok = from.every((r, i) => {
      const t = to[i]!;
      if (t.rel !== r.rel) return false;
      return sideSame(fn(m.f, r.l), t.l) && sideSame(fn(m.f, r.r), t.r);
    });
    if (ok) return m;
  }
  return null;
}

/**
 * Le pas qui transforme une relation établie en la ligne ; 'jump' : la ligne en découle,
 * mais en plusieurs opérations d'un coup (les deux membres changés, aucune règle).
 */
function transformStep(
  ks: KRecState,
  unit: RRel[],
  done: RRel[],
): { info: StepInfo; from: Source } | 'jump' | null {
  const to = stmtOf(unit.map((r) => ({ rel: r.rel, l: r.l, r: r.r })));
  let best: { info: StepInfo; score: number; from: Source } | null = null;
  let jump = false;
  let src: Source = 'line';
  const consider = (info: StepInfo, score: number) => {
    if (!best || score > best.score) best = { info, score, from: src };
  };
  for (const u of establishedUnits(ks, done)) {
    src = u.src;
    if (u.rels.length !== unit.length && unit.length > 1) continue;
    const from = stmtOf(u.rels.map((r) => ({ rel: r.rel, l: r.l, r: r.r })));
    const c = checkCached(ks, from, to);
    if (c.status !== 'valid' || c.relation === 'none') continue;
    if (c.ruleId) {
      consider(ruleInfo(c, to, from, u.rels, unit), 5);
      continue;
    }
    // un encadrement transformé maillon par maillon : la même règle partout
    if (u.rels.length === unit.length && unit.length > 1) {
      const per = u.rels.map((r, i) => checkCached(ks, stmtOf([r]), stmtOf([unit[i]!])));
      const ids = new Set(per.map((p) => p.ruleId ?? ''));
      if (per.every((p) => p.status === 'valid' && p.ruleId) && ids.size === 1) {
        consider(ruleInfo(per[0]!, to, from, u.rels, unit), 5);
        continue;
      }
    }
    const m = appliedFn(u.rels, unit);
    if (m) {
      consider(
        { id: RECURRENCE_STEP_IDS.increasing, card: CARD, label: m.label, hora: 2, why: m.why },
        4,
      );
      continue;
    }
    if (c.relation === 'implies') {
      const chain = unit.length > 1;
      consider(
        {
          id: RECURRENCE_STEP_IDS.weaken,
          card: CARD,
          label: chain ? 'Élargir l’encadrement' : 'Élargir l’inégalité',
          hora: 1,
          why: 'Ta ligne découle de la précédente : elle est moins précise, mais toujours vraie.',
        },
        1,
      );
      continue;
    }
    // un membre identique, l'autre calculé
    const calc =
      u.rels.length === unit.length &&
      u.rels.every((r, i) => {
        const t = unit[i]!;
        if (t.rel !== r.rel && !(t.rel === '=' && r.rel === '=')) return false;
        const direct = sideSame(r.l, t.l) && sideSame(r.r, t.r);
        const swapped = r.rel === '=' && sideSame(r.l, t.r) && sideSame(r.r, t.l);
        return direct || swapped;
      });
    if (calc) {
      consider(
        {
          id: RECURRENCE_STEP_IDS.compute,
          card: CARD,
          label: 'Calculer',
          hora: 1,
          why: 'Tu calcules un membre : la relation ne change pas.',
        },
        3,
      );
      continue;
    }
    jump = true;
  }
  const found = best as { info: StepInfo; score: number; from: Source } | null;
  return found ? { info: found.info, from: found.from } : jump ? 'jump' : null;
}

/* ------------------------------ but ------------------------------ */

/** la ligne écrit-elle P(n + 1) (même relation, ou même relation à un calcul près) ? */
function sameRelations(a: RRel[], b: RRel[]): boolean {
  return b.every((br) =>
    a.some((ar) => {
      if (ar.key === br.key) return true;
      if (ar.rel !== '=' || br.rel !== '=') return false;
      const da = sub(ar.l, ar.r);
      const db = sub(br.l, br.r);
      return compareTerms(da, db) === 'equal' || compareTerms(da, neg(db)) === 'equal';
    }),
  );
}

/** la ligne écrit-elle P(n + 1) telle quelle ? */
function isAnswerUnit(ks: KRecState, unit: RRel[], fact: RFact): boolean {
  const keys = new Set([...unit, ...fact.pairs].map((r) => r.key));
  return ks.answer.rels.every((r) => keys.has(r.key));
}

function reachesGoal(ks: KRecState, extra?: RFact): boolean {
  const est = establishedKeys(ks, extra?.pairs ?? []);
  return ks.answer.rels.every((r) => est.has(r.key));
}

export function krecGoalReached(ks: KRecState): boolean {
  return reachesGoal(ks);
}

/* ------------------------------ feuille ------------------------------ */

export function krecCommit(ks: KRecState, v: KRecVerdict): void {
  if (v.rfact && !v.unknown && (v.kind === 'step' || v.kind === 'ready' || v.kind === 'useless'))
    ks.lines.push(v.rfact);
}

/** La ligne (texte) est-elle établie (énoncé ou feuille) ? */
export function krecEstablished(ks: KRecState, text: string): boolean {
  try {
    const f = krecReadFact(text);
    const est = establishedKeys(ks);
    return f.rels.every((r) => est.has(r.key));
  } catch {
    return false;
  }
}

/** Le texte (choix du QCM) dit-il P(n + 1) ? (sans regarder la feuille) */
export function krecSameAsAnswer(ks: KRecState, text: string): boolean {
  try {
    // une chaîne vraie qui aboutit à P(n + 1) la dit aussi (« uₙ₊₁ = 3uₙ = … = 3ⁿ⁺¹ »)
    const f = krecReadFact(text);
    return sameRelations(f.pairs, ks.answer.rels) && krecTruth(ks, text) === 'true';
  } catch {
    return false;
  }
}

export function krecIsAnswer(ks: KRecState, text: string): boolean {
  return krecGoalReached(ks) && krecSameAsAnswer(ks, text);
}

/** La ligne (texte) est-elle vraie sachant l'énoncé ? 'true' | 'false' | 'unknown' (porte). */
export function krecTruth(ks: KRecState, text: string): 'true' | 'false' | 'unknown' {
  try {
    const f = krecReadFact(text);
    let unknown = false;
    for (const u of f.units) {
      const t = truthOf(ks, u);
      if (t.v === 'false') return 'false';
      if (t.v === 'unknown') unknown = true;
    }
    return unknown ? 'unknown' : 'true';
  } catch {
    return 'unknown';
  }
}
export function krecHolds(ks: KRecState, text: string): boolean {
  return krecTruth(ks, text) === 'true';
}

const chipsOf = (atoms: string[]): Chip[] => atoms.map((a) => ({ atoms: [a], text: a }));

/** Coup de pouce : la prochaine ligne du corrigé pas encore établie. */
export function krecNextMove(
  ks: KRecState,
): { line: Line; text: string; atoms: string[]; label: string } | null {
  if (krecGoalReached(ks)) return null;
  const next = ks.path.find((t) => !krecEstablished(ks, t)) ?? ks.spec.answer;
  const atoms = krecLineAtoms(next);
  const v = krecJudge(ks, chipsOf(atoms), { verify: true });
  return {
    line: parseText(next),
    text: next,
    atoms,
    label: v.kind === 'step' || v.kind === 'ready' ? (v.rule?.label ?? '') : '',
  };
}

/** Le corrigé rejoué ligne à ligne (porte, palette). */
export function krecSolvePath(model: ScratchModel): {
  lines: { text: string; line: Line; atoms: string[]; v: KRecVerdict }[];
  reached: boolean;
} {
  const ks = createKRecState(model);
  const lines = ks.path.map((text) => {
    const atoms = krecLineAtoms(text);
    const v = krecJudge(ks, chipsOf(atoms), { verify: true });
    if (v.kind === 'step') krecCommit(ks, v);
    return { text, line: parseText(text), atoms, v };
  });
  return { lines, reached: krecGoalReached(ks) };
}

/** Le texte de l'hypothèse de récurrence, pour l'énoncé de la feuille. */
export function recurrenceHypothesisText(model: ScratchModel): string | null {
  return model.kernel?.induction?.hypothesis ?? null;
}
